import React from "react";
import ReactApexChart from "react-apexcharts";
import { useTheme } from "@mui/material/styles";

const BarChart = ({
  categories = [],
  data = [],            // primary values shown on bars
  tooltipData = [],     // values shown in tooltip (e.g., kg)
  series = [],          // backward-compat: numbers => primary values, or Apex series[]
  height = 350,
  horizontal = false,
  title,
  seriesName = "Value",
  valueFormatter,       // (val, opt) => string
  tooltipValueFormatter, // (val, dataPointIndex, seriesIndex) => string
  chartId
}) => {
  const theme = useTheme();

    const numberFormatter = React.useMemo(
      () =>
        new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
      []
    );

    const specialTooltip = Array.isArray(tooltipData) && tooltipData.length > 0 && typeof tooltipData[0] === 'object' && (('order' in tooltipData[0]) || ('shipped' in tooltipData[0]));

  const chartCategories = categories.length ? categories : ["A", "B", "C"];
  const isNumberArray = Array.isArray(series) && series.length && typeof series[0] === "number";
  const isApexSeries = Array.isArray(series) && series.length && typeof series[0] === "object";

  const bars = data.length
    ? data
    : isNumberArray
    ? series
    : isApexSeries && Array.isArray(series[0]?.data)
    ? series[0].data
    : [120, 80, 45];

  const tData = Array.isArray(tooltipData) && tooltipData.length === bars.length ? tooltipData : bars;

  const hasMultiSeries = isApexSeries && series.length > 1;
  const normalizedSeries = hasMultiSeries ? series : [{ name: seriesName, data: bars }];

  const defaultColors = ["#7b8396", "#b7b5b5", "#d7e7fc"];
  const seriesColors = hasMultiSeries && Array.isArray(series)
    ? series.map((s) => s?.color).filter(Boolean)
    : defaultColors;

  const isDense = chartCategories.length > 7 || chartCategories.some((n) => String(n || "").length > 12);
  const areCategoriesNumeric = chartCategories.length > 0 && chartCategories.every((c) => {
    const s = String(c ?? "");
    return s !== "" && !isNaN(Number(s));
  });
  const horizontalBars = horizontal || !areCategoriesNumeric || isDense;

  const options = {
    chart: { type: "bar", toolbar: { show: false }, stacked: hasMultiSeries, id: chartId, destroyBeforeMount: true },
    theme: { mode: theme.palette.mode },
    colors: seriesColors.length ? seriesColors : defaultColors,
    plotOptions: {
      bar: {
        horizontal: horizontalBars,
        borderRadius: 4,
        dataLabels: { position: horizontalBars ? "center" : "top" }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opt) =>
       typeof valueFormatter === "function" ? valueFormatter(val, opt) : numberFormatter.format(val),
      offsetY: horizontalBars ? 0 : -14,
      offsetX: horizontalBars ?  10 : 0,
      style: { colors: [theme.palette.text.primary]}
    },
    xaxis: {
      categories: chartCategories,
      tickPlacement: "on",
      crosshairs: {
        show: true,
        position: "back",
        stroke: { color: theme.palette.divider, width: 1, dashArray: 3 }
      },
      tooltip: { enabled: !horizontalBars },
      labels: {
        show: true,
        rotate: horizontalBars ? 0 : -45,
        rotateAlways: true,
        trim: false,
        hideOverlappingLabels: false,
        style: { colors: theme.palette.text.secondary, fontSize: "11px" },
        formatter: horizontalBars ? (val) => numberFormatter.format(val) : undefined
      },
      axisBorder: { color: theme.palette.divider },
      axisTicks: { color: theme.palette.divider }
    },
    yaxis: { labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: horizontalBars ? undefined : (val) => numberFormatter.format(val)
      } },
    grid: { borderColor: theme.palette.divider },
    legend: {
      show: normalizedSeries.length > 1,
      position: "bottom",
      labels: { colors: theme.palette.text.secondary }
    },
   tooltip: (
     specialTooltip
       ? {
           shared: false,
           intersect: false,
           followCursor: true,
           appendTo: 'body',
           offsetY: -10,
           custom: ({ series, seriesIndex, dataPointIndex, w }) => {
             const d = tooltipData[dataPointIndex] || {};
             const label = w?.config?.xaxis?.categories?.[dataPointIndex] ?? "";
             const order = Number(d.order ?? d.total ?? 0) || 0;
             const shipped = Number(d.shipped ?? 0) || 0;
             const remaining = order - shipped;
             const so = numberFormatter.format(order);
             const sh = numberFormatter.format(shipped);
             const rem = numberFormatter.format(remaining);
             const pct = order === 0 ? "—" : `${((shipped / order) * 100).toFixed(1)}%`;
             const labelColor = theme.palette.text.primary;
             const subColor = theme.palette.text.secondary;
             const shippedColor = theme.palette.error.main || '#f44336';
             const remColor = remaining < 0 ? theme.palette.error.main || '#f44336' : theme.palette.text.primary;

             return (
               `<div class="apexcharts-tooltip-box" style="padding:10px; font-family: Inter, Roboto, Arial, sans-serif; background: ${theme.palette.background.paper}; color: ${theme.palette.text.primary}; box-shadow: 0 6px 18px rgba(0,0,0,0.08); border-radius:6px;">
                 <div style="font-weight:700;margin-bottom:6px;color:${labelColor};">${String(label)}</div>
                 <div style="font-size:0.85rem;color:${subColor}">Ordered: <strong style=\"color:${labelColor}\">${so}</strong></div>
                 <div style="font-size:0.85rem;color:${subColor}">Shipped: <strong style=\"color:${shippedColor}\">${sh}</strong></div>
                 <div style="font-size:0.85rem;color:${subColor}">Remaining: <strong style=\"color:${remColor}\">${rem}</strong></div>
                 <div style="font-size:0.78rem;color:${subColor};margin-top:6px">Percent shipped: <strong>${pct}</strong></div>
               </div>`
             );
           }
         }
       : {
           shared: hasMultiSeries,
           intersect: false,
           followCursor: false,
           appendTo: 'body',
           y: {
             formatter: (val, { dataPointIndex, seriesIndex }) => {
               if (hasMultiSeries) {
                 return typeof tooltipValueFormatter === "function"
                   ? tooltipValueFormatter(val, dataPointIndex, seriesIndex)
                   : numberFormatter.format(val);
               }
               const v = tData[dataPointIndex];
               return typeof tooltipValueFormatter === "function"
                 ? tooltipValueFormatter(v, dataPointIndex, 0)
                 : numberFormatter.format(v);
             }
           }
         }
   ),
    title: { text: title ?? "", style: { color: theme.palette.text.primary } },
    subtitle: { text: "" },
    responsive: [{ breakpoint: 600, options: { dataLabels: { offsetY: horizontalBars ? 0 : -10 }, chart: { height: Math.min(300, height) } } }]
  };

  return <ReactApexChart options={options} series={normalizedSeries} type="bar" height={height} />;
};

export default BarChart;